import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, inject, Injector } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { EnvUrls, Urls } from '..';
import { FipSessionTimeoutComponent } from '../fip-session-timeout.component';
import { SessionConfig } from '../session-config';
import { LOCATION, WINDOW } from '../window.token';
import { FipService } from './fip.store.service';
import { cacheService } from 'lib-services';
type FipState = {
  config: SessionConfig | null;
  currentEnv:
    | (Omit<SessionConfig, 'urls'> & {
        urls: Urls;
      })
    | null;
  userInformation: any;
  maxSessionTime: number;
  isShowModal: boolean;
  remainingTime: number;
  forceLogout: boolean;
  overlayRef: any;
  expiresInMilliSeconds: number;
  sessionExists: boolean;
  expirationTime: string | null;
  sessionWatcherStarted: boolean;
  goodSession: boolean;
  expiring: boolean;
  userActivityCleanup: () => void | null;
  activeUserAction: boolean;
  visibilityCleanup: () => void | null;
  sessionWorker: Worker | null;
};

const initialState: FipState = {
  config: null,
  currentEnv: null,
  userInformation: null,
  maxSessionTime: 0,
  isShowModal: false,
  remainingTime: 0,
  forceLogout: false,
  overlayRef: null,
  expiresInMilliSeconds: 0,
  sessionExists: false,
  expirationTime: null,
  sessionWatcherStarted: false,
  goodSession: false,
  expiring: false,
  userActivityCleanup: null,
  activeUserAction: false,
  visibilityCleanup: null,
  sessionWorker: null,
};
const HALF_HOUR_IN_MS = 0.5 * 60 * 60 * 1000;
const TWO_HOURS_IN_MS = 2 * 60 * 60 * 1000;
const THREE_MINUTES_IN_MS = 3 * 60 * 1000;
const ONE_MINUTES_IN_MS = 60 * 1000; // I will use this for testing
export const FipStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    privNameMap: computed(() => {
      const info = store.userInformation();
      if (!info || typeof info !== 'object') return {};

      if (store.currentEnv()?.identityProviderRealm === 'ews') {
        return Object.keys(info).reduce<Record<string, string>>((prev, key) => {
          prev[key.toLowerCase()] = key;
          return prev;
        }, {});
      } else {
        return (info.groups || []).reduce((prev: any, key: any) => {
          prev[key.toLowerCase()] = key;
          return prev;
        }, {});
      }
    }),
  })),
  withMethods(
    (
      store,
      overlay = inject(Overlay),
      window = inject(WINDOW),
      location = inject(LOCATION),
      injector = inject(Injector),
      service = inject(FipService),
    ) => {
      return {
        async _fetchUserInfo(): Promise<any> {
          if (cacheService.has('userInfo')) {
            console.log('@finra/fip-session: getting userinfo from cache');
            const userInformation = cacheService.get('userInfo').value;
            patchState(store, { userInformation });
            return new Promise((res) => res(userInformation));
          }
          const url = store.currentEnv()?.urls.userInfo || '';
          try {
            if (url.length === 0) {
              console.log('@finra/fip-session: no user info url set.');
              patchState(store, { userInformation: null });
              return new Promise((res) => res(null));
            }
            console.log('@finra/fip-session: getting user info.');
            const response = await service.fetch(url, store.currentEnv()?.env).catch((err) => {
              const status = err?.response?.status || err?.status || 'unknown';
              console.error(`@finra/fip-session: FetchUserInfo failed ${status}`, err);
              throw new Error(`fetchUserInfo failed`);
            });
            const userInformation = await response.json();
            patchState(store, { userInformation });
            console.log('@finra/fip-session: User currently valid. Application will be initialized.');
            cacheService.set('userInfo', userInformation);
            return new Promise((res) => res(userInformation));
          } catch (err) {
            console.error('@finra/fip-session: User fetch failed-' + url, err);
            this._handleError(err);
          }
        },
        async _fetchSession(): Promise<void> {
          const url = store.currentEnv()?.urls.sessionTime || '';
          try {
            console.log(`@finra/fip-session: Getting session timeout info. [${new Date().toLocaleTimeString()}]`);
            const response = await service.fetch(url, store.currentEnv()?.env).catch((err) => {
              throw new Error(`fetchSession Response status: ${err?.response?.status}`);
            });
            const { expiresInMilliSeconds, sessionExists, expirationTime } = await response.json();
            const thresholdMs = store.currentEnv()?.thresholdMs || 0;
            const goodSession = expiresInMilliSeconds > thresholdMs || sessionExists === 'Y';
            const expiring = expiresInMilliSeconds <= thresholdMs;
            console.log(
              `@finra/fip-session: expiring check - ${expiresInMilliSeconds}ms <= ${thresholdMs}ms = ${expiring} [${new Date().toLocaleTimeString()}]`,
            );
            patchState(store, {
              expiresInMilliSeconds,
              sessionExists: sessionExists === 'Y',
              expirationTime,
              goodSession,
              expiring,
            });
          } catch (err) {
            this._handleError(err);
          }
        },
        _updateConfig(config: SessionConfig): void {
          try {
            const urls = {
              ews: {
                dev: {
                  get login() {
                    return `https://ews.int.finra.org/auth/logon?realm=ews&goto=${window.location.toString()}`;
                  },
                  logout: 'https://ews.int.finra.org/auth/ews_logout.html',
                  sessionTime: 'https://ews.int.finra.org/auth/rest/v2/sessions/info',
                  userInfo: 'https://ews-devint.fip.dev.finra.org/fip/rest/ews/users/userinfo',
                },
                qa: {
                  get login() {
                    return `https://ews.qa.finra.org/auth/logon?realm=ews&goto=${window.location.toString()}`;
                  },
                  logout: 'https://ews.qa.finra.org/auth/ews_logout.html',
                  sessionTime: 'https://ews.qa.finra.org/auth/rest/v2/sessions/info',
                  userInfo: 'https://ews-qaint.fip.qa.finra.org/fip/rest/ews/users/userinfo',
                },
                prod: {
                  get login() {
                    return `https://ews.finra.org/auth/logon?realm=ews&goto=${window.location.toString()}`;
                  },
                  logout: 'https://ews.finra.org/auth/ews_logout.html',
                  sessionTime: 'https://ews.finra.org/auth/rest/v2/sessions/info',
                  userInfo: 'https://ews.fip.finra.org/fip/rest/ews/users/userinfo',
                },
                local: {
                  get login() {
                    return `https://localhost:8000/fip/?realm=isso&goto=${window.location.toString()}`;
                  },
                  logout: '/logout',
                  sessionTime: 'http://localhost:8080/api/app-shell/expiration-time',
                  userInfo: 'http://localhost:8080/api/app-shell/expiration-time',
                },
              },
              isso: {
                dev: {
                  get login() {
                    return `https://isso-devint.fip.dev.finra.org/fip/?realm=isso&goto=${window.location.toString()}`;
                  },
                  logout: '',
                  sessionTime: 'https://isso-devint.fip.dev.finra.org/fip/rest/v2/sessions/info',
                  userInfo: 'https://isso-devint.fip.dev.finra.org/fip/rest/isso/users/userinfo',
                },
                qa: {
                  get login() {
                    return `https://isso-qaint.fip.qa.finra.org/fip/?realm=isso&goto=${window.location.toString()}`;
                  },
                  logout: '',
                  sessionTime: 'https://isso-qaint.fip.qa.finra.org/fip/rest/v2/sessions/info',
                  userInfo: 'https://isso-qaint.fip.qa.finra.org/fip/rest/isso/users/userinfo',
                },
                prod: {
                  get login() {
                    return `https://isso.fip.finra.org/fip/?realm=isso&goto=${window.location.toString()}`;
                  },
                  logout: '',
                  sessionTime: 'https://isso.fip.finra.org/fip/rest/v2/sessions/info',
                  userInfo: 'https://isso.fip.finra.org/fip/rest/isso/users/userinfo',
                },
                local: {
                  get login() {
                    return `https://localhost:8000/fip/?realm=isso&goto=${window.location.toString()}`;
                  },
                  logout: '',
                  sessionTime: 'https://localhost:8000/fip/rest/v2/sessions/info',
                  userInfo: 'https://localhost:8000/fip/rest/isso/users/userinfo',
                },
              },
            };
            const _config = {
              ...{
                maxSessionTimeInMs:
                  !config.identityProviderRealm || config.identityProviderRealm === 'ews'
                    ? HALF_HOUR_IN_MS
                    : TWO_HOURS_IN_MS,
                thresholdMs: THREE_MINUTES_IN_MS,
                identityProviderRealm: 'ews',
                thresholdMethod: 'GET',
                sessionMonitoringStrategy: undefined, // Defaults to 'periodic' if not specified
                periodicCheckInterval: undefined, // Defaults to 2 mins if periodic strategy is used
                urls,
              },
              ...config,
              get env() {
                let env = 'prod';

                if (config.env.toLowerCase().includes('dev')) {
                  env = 'dev';
                } else if (config.env.toLowerCase().includes('qa')) {
                  env = 'qa';
                } else if (config.env.toLowerCase().includes('local')) {
                  env = 'local';
                }

                return env;
              },
            } as SessionConfig;
            console.log('@finra/fip-session: session config', _config);
            function geturls() {
              if (!_config.urls) return null;
              if (!_config.identityProviderRealm) return null;

              const realmUrls = _config.urls[_config.identityProviderRealm as keyof typeof _config.urls];
              if (!realmUrls) return null;

              return realmUrls[_config.env as keyof EnvUrls];
            }
            //  this.currentEnv = { ...this.config, urls: this.config.urls[this.config.identityProviderRealm][this.config.env] };
            const currentEnv = { ..._config, urls: geturls() } as any;
            patchState(store, { config: _config, currentEnv, maxSessionTime: _config.maxSessionTimeInMs });
          } catch (err) {
            console.error('@finra/fip-session: unable to set config', err);
          }
        },
        // This method processes and validates the user's session status
        // It performs the following steps:
        // 1. Gets current environment config and threshold time
        // 2. Fetches latest session information from server
        // 3. Checks if session is valid by comparing expiration time against threshold
        // 4. Updates the max session time in store
        // 5. Handles different session states:
        //    - Valid session: Returns success
        //    - Expired/non-existent session: Triggers logout for EWS users
        //    - Expiring soon: Opens warning modal for EWS users or refreshes session for others
        async _processSession(): Promise<any> {
          const currentEnv = store.currentEnv();
          await this._fetchSession();

          const expiresInMilliSeconds = store.expiresInMilliSeconds();
          const goodSession = store.goodSession();
          const expiring = store.expiring();

          if (goodSession && !expiring) {
            //continue
            console.log(`@finra/fip-session: setting timeout check delay. ${expiresInMilliSeconds}ms`);
            return new Promise((res) => res({ goodSession, expiring }));
          }
          if (!goodSession) {
            console.log('@finra/fip-session: session has timed out or session does not exist. Will log out.');
            if (currentEnv?.identityProviderRealm === 'ews') {
              this.logout();
            } else {
              this._openModal(10000, true);
            }
            return new Promise((res) => res({ goodSession, expiring }));
          }
          if (expiring) {
            const isEws = currentEnv?.identityProviderRealm === 'ews';
            if (store.activeUserAction() || !isEws) {
              console.log(`@finra/fip-session: refreshing session  [${new Date().toLocaleTimeString()}]`);
              await this.refreshUserSession(isEws);
            } else {
              const expiresIn = Math.min(expiresInMilliSeconds, store.currentEnv()?.thresholdMs || 0);
              console.log(
                `@finra/fip-session: threshold reached, opening timeout modal.  [${new Date().toLocaleTimeString()}]`,
                expiresIn,
              );
              this._openModal(expiresIn);
            }
          }
          return new Promise((res) => res({ goodSession, expiring }));
        },
        _startSessionWatcher() {
          if (store.config()?.disableSessionTimer) {
            console.log('@finra/fip-session: session watcher is disabled');
            return;
          }
          // Prevent multiple watchers from running
          if (store.sessionWatcherStarted()) {
            console.log(`@finra/fip-session: session watcher already running. [${new Date().toLocaleTimeString()}]`);
            return;
          }
          console.log(`@finra/fip-session: session timer hot [${new Date().toLocaleTimeString()}]`);
          const msToTime = (ms: number) => `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
          const nextRunTime = Math.min(
            store.maxSessionTime() || 0,
            store.expiresInMilliSeconds() - (store.currentEnv()?.thresholdMs ?? 0),
          );
          const nextRunAt = new Date(Date.now() + nextRunTime);
          console.log(
            `@finra/fip-session: processing session timeout info. expiresIn - ${msToTime(store.expiresInMilliSeconds())} threshold - ${msToTime(store.currentEnv()?.thresholdMs || 0)} nextRun - ${msToTime(nextRunTime)} at ${nextRunAt.toLocaleTimeString()} [${new Date().toLocaleTimeString()}]`,
          );

          patchState(store, { sessionWatcherStarted: true }); // ✅ Prevent duplicate execution

          if (store.config()?.enableActivityTracking) {
            this._setupUserActivityTracking();
          }

          this._setupVisibilityChangeTracking();

          // Use Web Worker for background monitoring (not throttled)
          this._startWorkerMonitoring();
        },

        /**
         * Sets up event listeners to track user activity
         *
         * @private
         */
        _setupUserActivityTracking() {
          const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
          let hasActivity = false;
          let checkInterval: any = null;
          const trackActivity = () => {
            hasActivity = true;
          };

          activityEvents.forEach((eventType) => {
            window.addEventListener(eventType, trackActivity, { passive: true });
          });

          checkInterval = setInterval(() => {
            if (hasActivity) {
              patchState(store, { activeUserAction: true });
              hasActivity = false;
            } else {
              patchState(store, { activeUserAction: false });
            }
          }, 30000);

          const cleanup = () => {
            activityEvents.forEach((eventType) => {
              window.removeEventListener(eventType, trackActivity);
            });
            if (checkInterval) {
              clearInterval(checkInterval);
            }
          };

          patchState(store, {
            userActivityCleanup: cleanup,
          });
        },

        _setupVisibilityChangeTracking() {
          const handleVisibilityChange = () => {
            const visibilityState = window.document.hidden ? 'hidden' : 'visible';
            const timestamp = new Date().toLocaleTimeString();
            console.log(`@finra/fip-session: [${timestamp}] Tab became ${visibilityState}`);

            if (!window.document.hidden) {
              console.log(`@finra/fip-session: [${timestamp}] Checking session state after tab became visible`);
              console.log(
                `@finra/fip-session: goodSession=${store.goodSession()}, expiring=${store.expiring()}, expiresIn=${store.expiresInMilliSeconds()}ms`,
              );

              if (!store.goodSession()) {
                console.log(
                  `@finra/fip-session: [${timestamp}] Session expired while tab was hidden - triggering logout`,
                );
                if (store.currentEnv()?.identityProviderRealm === 'ews') {
                  this.logout();
                } else {
                  this._openModal(10000, true);
                }
              }
            }
          };

          window.document.addEventListener('visibilitychange', handleVisibilityChange);

          const cleanup = () => {
            window.document.removeEventListener('visibilitychange', handleVisibilityChange);
          };

          patchState(store, {
            visibilityCleanup: cleanup,
          });
        },

        _startWorkerMonitoring() {
          try {
            const worker = new Worker(new URL('../session.worker', import.meta.url), { type: 'module' });

            worker.onmessage = async (e: MessageEvent) => {
              const { type, payload } = e.data;
              const timestamp = new Date().toLocaleTimeString();
              console.log(`@finra/fip-session: [${timestamp}] [Worker Message] ${type}`, payload);

              switch (type) {
                case 'SESSION_STATUS':
                  console.log(
                    `@finra/fip-session: [${timestamp}] Updating state: expires=${payload.expiresInMilliSeconds}ms, good=${payload.goodSession}, expiring=${payload.expiring}`,
                  );
                  patchState(store, {
                    expiresInMilliSeconds: payload.expiresInMilliSeconds,
                    sessionExists: payload.sessionExists,
                    goodSession: payload.goodSession,
                    expiring: payload.expiring,
                  });
                  break;
                case 'SESSION_EXPIRED':
                  console.log(`@finra/fip-session: [${timestamp}] Session expired - initiating logout`);
                  if (store.currentEnv()?.identityProviderRealm === 'ews') {
                    this.logout();
                  } else {
                    this._openModal(10000, true);
                  }
                  this._stopWorkerMonitoring();
                  break;
                case 'SESSION_EXPIRING':
                  console.log(`@finra/fip-session: [${timestamp}] Session expiring - checking for refresh`);
                  const isEws = store.currentEnv()?.identityProviderRealm === 'ews';
                  if (store.activeUserAction() || !isEws) {
                    console.log(
                      `@finra/fip-session: [${timestamp}] Auto-refreshing session (userActive=${store.activeUserAction()}, isEws=${isEws})`,
                    );
                    await this.refreshUserSession(isEws);
                  } else {
                    console.log(`@finra/fip-session: [${timestamp}] Opening warning modal`);
                    this._openModal(payload.expiresInMilliSeconds);
                  }
                  break;
                case 'ERROR':
                  console.error(`@finra/fip-session: [${timestamp}] Worker error`, payload);
                  break;
              }
            };

            worker.onerror = (error) => {
              console.error('@finra/fip-session: Worker error', error);
            };

            const strategy = store.config()?.sessionMonitoringStrategy || 'periodic';
            const checkInterval = strategy === 'periodic' ? store.config()?.periodicCheckInterval || 120000 : 60000;

            worker.postMessage({
              type: 'START',
              payload: {
                sessionTimeUrl: store.currentEnv()?.urls.sessionTime,
                checkInterval,
                thresholdMs: store.currentEnv()?.thresholdMs,
                env: store.currentEnv()?.env,
                credentials: store.currentEnv()?.env !== 'local' ? 'include' : 'same-origin',
              },
            });

            patchState(store, { sessionWorker: worker });
            console.log(`@finra/fip-session: Worker started with ${strategy} strategy, interval: ${checkInterval}ms`);
          } catch (error) {
            console.error('@finra/fip-session: Failed to start worker, falling back to setTimeout', error);
            this._watchSession();
          }
        },

        _stopWorkerMonitoring() {
          const worker = store.sessionWorker();
          if (worker) {
            worker.postMessage({ type: 'STOP' });
            worker.terminate();
            patchState(store, { sessionWorker: null, sessionWatcherStarted: false });
            console.log('@finra/fip-session: Worker stopped');
          }
          if (store.userActivityCleanup) {
            store.userActivityCleanup();
          }
          if (store.visibilityCleanup) {
            store.visibilityCleanup();
          }
        },

        async _watchSession() {
          try {
            const strategy = store.config()?.sessionMonitoringStrategy === 'periodic' ? 'periodic' : 'dynamic';
            const periodicInterval = store.config()?.periodicCheckInterval || 120000; // 2 minutes default

            console.log(
              `@finra/fip-session: new fip session service used - strategy: ${strategy}, interval(only for periodic): ${periodicInterval}ms`,
            );

            while (store.maxSessionTime() > 0) {
              console.log(`@finra/fip-session: processing session... [${new Date().toLocaleTimeString()}]`);
              const { goodSession, expiring } = await this._processSession();
              if (!goodSession || expiring) {
                console.log(`@finra/fip-session: session watcher stopping [${new Date().toLocaleTimeString()}]`);
                if (store.userActivityCleanup) {
                  store.userActivityCleanup();
                }
                if (store.visibilityCleanup) {
                  store.visibilityCleanup();
                }
                patchState(store, { sessionWatcherStarted: false }); // ✅ Reset flag when session expires
                break;
              }
              const msToTime = (ms: number) => `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
              console.log(
                `@finra/fip-session:store.maxSessionTime() ${msToTime(store.maxSessionTime())} [${new Date().toLocaleTimeString()}]`,
              );
              console.log(
                `@finra/fip-session:store.expiresInMilliSeconds() [${new Date().toLocaleTimeString()}]`,
                msToTime(store.expiresInMilliSeconds()),
              );
              console.log(
                `@finra/fip-session:store.thresholdMs() [${new Date().toLocaleTimeString()}]`,
                msToTime(store.currentEnv()?.thresholdMs),
              );

              let reloadTime: number;
              if (strategy === 'periodic') {
                reloadTime = periodicInterval;
                console.log(
                  `@finra/fip-session: periodic session check in ${msToTime(reloadTime)} at ${new Date().toLocaleTimeString()}`,
                );
              } else {
                // Dynamic strategy: calculate optimal check time based on session expiration
                const reloadTimeMs = Math.min(
                  store.maxSessionTime() || 0,
                  store.expiresInMilliSeconds() - (store.currentEnv()?.thresholdMs ?? 0),
                );
                reloadTime = Math.floor(reloadTimeMs / 60000) * 60000; // Round down to nearest minute
                // Ensure minimum reload time of 1 minute for dynamic strategy
                if (reloadTime <= 0) {
                  reloadTime = 60000; // 1 minute minimum
                }
                console.log(
                  `@finra/fip-session: dynamic session check in ${msToTime(reloadTime)} now ${new Date().toLocaleTimeString()}`,
                );
              }

              if (reloadTime <= 0) {
                console.log(
                  `@finra/fip-session: session expired. stopping watcher. [${new Date().toLocaleTimeString()}]`,
                );
                if (store.visibilityCleanup) {
                  store.visibilityCleanup();
                }
                patchState(store, { sessionWatcherStarted: false }); // ✅ Reset flag when session expires
                break;
              }

              await new Promise((resolve) => setTimeout(resolve, reloadTime));
            }
          } catch (err) {
            this._openModal(10000, true);
            console.error(`@finra/fip-session: error in session watcher [${new Date().toLocaleTimeString()}]`, err);
          }
        },
        async load(config: SessionConfig): Promise<any> {
          if (!config && !store.config()) {
            console.warn(
              `@finra/fip-session: no Session Config Found1. Skipping Session Service Initialization [${new Date().toLocaleTimeString()}]`,
            );
            return Promise.resolve(
              '@finra/fip-session: no Session Config Found1. Skipping Session Service Initialization',
            );
          }
          this._updateConfig(config);

          try {
            if (!store.config()) {
              console.warn(
                `@finra/fip-session: no Session Config Found2. Skipping Session Service Initialization [${new Date().toLocaleTimeString()}]`,
              );
              return Promise.resolve(
                '@finra/fip-session: no Session Config Found2. Skipping Session Service Initialization',
              );
            }
            console.log(
              `@finra/fip-session: session config found [${new Date().toLocaleTimeString()}]`,
              store.config(),
            );
            console.log(`@finra/fip-session: session env set [${new Date().toLocaleTimeString()}]`, store.currentEnv());
            const { goodSession, expiring } = await this._processSession();
            if (!goodSession) {
              console.log(`@finra/fip-session: Attempting Login [${new Date().toLocaleTimeString()}]`);
              this._login();
              return new Error('@finra/fip-session: User is not logged in. Redirecting user to FIP login.');
            } else {
              console.log(`@finra/fip-session: about to get user info [${new Date().toLocaleTimeString()}]`);
              await this._fetchUserInfo();
              if (!expiring) {
                this._startSessionWatcher();
              }
              return new Promise((res) => res(store.userInformation()));
            }
          } catch (error) {
            console.error(`@finra/fip-session error in load: [${new Date().toLocaleTimeString()}]`, error);
            this._openModal(10000, true);
          }
        },
        logout() {
          cacheService.clear();
          location.href = store.currentEnv()?.urls.logout || '';
        },
        _login() {
          location.href = store.currentEnv()?.urls.login || '';
        },

        _openModal(remainingTime: number = 0, forceLogout = false) {
          let overlayRef = store.overlayRef();
          if (overlayRef) {
            overlayRef.dispose();
          }
          overlayRef = overlay.create({
            hasBackdrop: true,
            panelClass: ['modal', 'is-active'],
            backdropClass: 'modal-background',
            disposeOnNavigation: true,
            positionStrategy: overlay.position().global().centerHorizontally().centerVertically(),
          });
          const _injector = Injector.create({
            parent: injector,
            providers: [
              {
                provide: 'forceLogout',
                useValue: forceLogout,
              },
              {
                provide: 'remainingTime',
                useValue: remainingTime,
              },
              {
                provide: 'overlayRef',
                useValue: overlayRef,
              },
            ],
          });

          overlayRef.attach(new ComponentPortal(FipSessionTimeoutComponent, null, _injector));
          patchState(store, { overlayRef, forceLogout, remainingTime });
        },
        async refreshUserSession(restartTimer = false) {
          const currentEnv = store.currentEnv();
          const url = currentEnv?.refreshUrl || '';
          console.log(
            `@finra/fip-session: extending user session,${currentEnv?.refreshMethod}-${url} [${new Date().toLocaleTimeString()}]`,
          );

          if (!url) {
            console.error(
              `@finra/fip-session: No refresh url found. Initiating FIP ISSO Login. [${new Date().toLocaleTimeString()}]`,
            );
            this._login();
            return;
          }
          patchState(store, { sessionWatcherStarted: false }); //stop the timer
          try {
            const response = await service.fetch(url, store.currentEnv()?.env, currentEnv?.refreshMethod || 'GET');
            console.log(`refreshUserSession Response status: ${response.status} [${new Date().toLocaleTimeString()}]`);
            if (!response.ok) {
              throw new Error('refreshUserSession failed');
            }
            patchState(store, { maxSessionTime: store.currentEnv()?.maxSessionTimeInMs, expiring: false });
            if (restartTimer) this._startSessionWatcher();
          } catch (err) {
            if (currentEnv?.identityProviderRealm === 'ews') {
              console.error(
                `@finra/fip-session: Unable to reauthenticate user. Logging out [${new Date().toLocaleTimeString()}]`,
              );
              this._openModal(10000, true);
              return; // Exit early to prevent session watcher from restarting
            } else {
              console.error(
                `@finra/fip-session: Unable to refresh user session. Initiating FIP ISSO Login. [${new Date().toLocaleTimeString()}]`,
              );
              this._login();
              return; // Exit early to prevent session watcher from restarting
            }
          }
        },

        _handleError: (error: any) => {
          let errMsg = '';
          // if status assume it is a HttpError
          errMsg = error.status ? `${error.status} - ${error.statusText}` : error.message;
          console.error(`@finra/fip-session error: ${errMsg} [${new Date().toLocaleTimeString()}]`); // log to console instead

          if (error.stack) {
            console.error(error.stack);
          }

          throw new Error(error);
        },
      };
    },
  ),
);
